import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Loader, SpendingAccountsContainer, Title, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { getLsaItems } from '../../../actions/accounts.js';
import permissionLevel from '../../../services/permissions.js';
import AllotmentsContainer from './AllotmentsContainer.js';
import { changeClaimStatus, reassignClaim } from '../../../actions/adjudication.js';

/**
* SpendingAccounts component manages the display and interaction with user spending accounts.
* Handles both Health Spending Account (HSA) and Lifestyle Spending Account (LSA) functionality,
* including claim creation, allotment management, and account navigation.
* 
* @component
* @param {Object} props - Component props
* @param {Object} props.history - React Router history object for navigation
* @param {Function} props.dispatch - Redux dispatch function for triggering actions
* @param {Array} props.lifestyleItems - Array of lifestyle spending items/categories
* @param {Array} props.healthItems - Array of health spending items/categories
* @param {Object} props.coverage - User's coverage information including spending account details
* @param {Array} props.dependents - Array of user's dependents for claim purposes
* @param {Array} props.hsaClaimTypes - Available Health Spending Account claim types
* @param {Object} props.currentUser - Current authenticated user object with meta, spending, and account info
* @param {string} props.accessType - User's access level/permissions type
* @param {Object} props.loader - Loading state object with isLoading boolean
* @returns {JSX.Element} The rendered SpendingAccounts component
*/
const SpendingAccounts = ({
    history, 
    dispatch, 
    lifestyleItems, 
    healthItems, 
    coverage, 
    dependents,
    hsaClaimTypes,
    currentUser,
    accessType,
    loader,
    adjudicators
}) => {
    const hasReassignPermission = Boolean(permissionLevel('Reassign Claim', accessType).includes('write'));

    useEffect(() => {
        if (!permissionLevel('Coverage', accessType).includes('read')) {
            window.location.href = '/403';
        }
    }, []);

    return (
        <>
            {
                loader?.isLoading
                    ? (
                        <Loader/>
                    )
                    : null
            }

            <Title 
                title={ i18n.t(tokens.titles.spendingAccounts) } 
                customStyle={{ flex: 1 }} 
                titlestyle={{ marginBottom: 0 }} 
            />
            <AllotmentsContainer />

            <SpendingAccountsContainer 
                meta={currentUser.meta}
                addBanking={() => history.push('/my-account')}
                submitLifestyleClaim={null}
                submitHealthClaim={null}
                lifestyleItems={lifestyleItems}
                healthItems={healthItems}
                getLsaItems={ () => dispatch(getLsaItems(currentUser.meta.accountID)) }
                coverage={coverage.spendingAccounts}
                maximums={currentUser.spending}
                dependents={dependents}
                hsaClaimTypes={hsaClaimTypes}
                lsaClaimTypes={currentUser.spending.lsaClaimTypes}
                isMasterSite={true}
                reassignClaim={(...params) => dispatch(reassignClaim(...params))}
                changeClaimStatus={(...params) => dispatch(changeClaimStatus(...params))}
                hasReassignPermission={hasReassignPermission}
                adjudicators={adjudicators}
                loader={loader}
            />
        </>
    );
};

SpendingAccounts.propTypes = {
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentUser: state.users.currentUser,
    dependents: state.users.currentUser.dependents,
    accessType: state.master.access,
    hsaClaimTypes: state.master.claimTypes,
    adjudicators: state.master.adjudicators,
    healthItems: state.users.currentUser.spending.health.items,
    lifestyleItems: state.users.currentUser.spending.lifestyle.items,
    master: state.master
});

export default (connect(mapStateToProps)(SpendingAccounts));